import React, { useEffect, useState, useRef } from 'react';
import {
  ColorInput,
  DividerRight,
  EnhancedSlider,
  GridDropdown,
  GridDropInput,
  GridTextInput,
  MultiSelectionIcon,
} from '../../utils/gridUtils';
import { TextInitialData } from '../../utils/constant';
import { handleAddText } from '../gridpagefunction/Text';
// Local fonts available offline - organized by type and style
const localFonts = [
  // === SANS-SERIF FONTS ===
  {
    family: 'Helvetiker',
    variants: ['regular', 'bold'],
    type: 'Sans-serif',
    description: 'Clean, modern sans-serif',
  },
  // {
  //   family: 'Droid Sans',
  //   variants: ['regular'],
  //   type: 'Sans-serif',
  //   description: 'Android system font',
  // },

  // === SERIF FONTS ===
  {
    family: 'Gentilis',
    variants: ['regular', 'bold'],
    type: 'Serif',
    description: 'Classic serif with elegance',
  },
  // {
  //   family: 'Droid Serif',
  //   variants: ['regular'],
  //   type: 'Serif',
  //   description: 'Android serif font',
  // },

  // === MONOSPACE FONTS ===
  {
    family: 'Optimer',
    variants: ['regular', 'bold'],
    type: 'Monospace',
    description: 'Technical monospace font',
  },
  // {
  //   family: 'Droid Sans Mono',
  //   variants: ['regular'],
  //   type: 'Monospace',
  //   description: 'Android monospace font',
  // },

  // === DISPLAY FONTS ===
  {
    family: 'Helvetiker Bold',
    variants: ['bold'],
    type: 'Display',
    description: 'Bold display font',
  },
  {
    family: 'Gentilis Bold',
    variants: ['bold'],
    type: 'Display',
    description: 'Bold serif display',
  },
  {
    family: 'Optimer Bold',
    variants: ['bold'],
    type: 'Display',
    description: 'Bold monospace display',
  },

  // === NEW GOOGLE FONTS ===
  {
    family: 'Roboto',
    variants: ['regular'],
    type: 'Sans-serif',
    description: 'Popular modern sans-serif',
  },
  {
    family: 'Open Sans',
    variants: ['regular'],
    type: 'Sans-serif',
    description: 'Friendly, neutral sans-serif',
  },
  {
    family: 'Montserrat',
    variants: ['regular'],
    type: 'Sans-serif',
    description: 'Geometric sans-serif',
  },
  {
    family: 'Cinzel',
    variants: ['regular'],
    type: 'Serif',
    description: 'Classical Roman style',
  },
  {
    family: 'Indie Flower',
    variants: ['regular'],
    type: 'Handwritten',
    description: 'Carefree, bubbly handwriting',
  },
  {
    family: 'Tangerine',
    variants: ['regular'],
    type: 'Calligraphy',
    description: 'Elegant 17th-century calligraphy',
  },
  {
    family: 'New Rocker',
    variants: ['regular'],
    type: 'Display',
    description: 'Harsh, screaming gothic font',
  },
  {
    family: 'Leckerli One',
    variants: ['regular'],
    type: 'Script',
    description: 'Cookbook style cursive',
  },
  {
    family: 'Allura',
    variants: ['regular'],
    type: 'Signature',
    description: 'Stylish script signature',
  },
  {
    family: 'Dancing Script',
    variants: ['regular'],
    type: 'Cursive',
    description: 'Lively casual script',
  },
  {
    family: 'Press Start 2P',
    variants: ['regular'],
    type: 'Pixel',
    description: 'Classic 8-bit gaming font',
  },
];
import { getVariantLabel } from '../../utils/UtilsFunction';
import { isGridHistorySuppressed } from '../../utils/gridHistorySuppress';

const TextComponent = ({ object, historyUiRevision = 0 }) => {
  let { selectedObject } = object;

  const [addData, setAddData] = useState(() => {
    const existingMaterial = selectedObject?.userData?.text_material;
    if (selectedObject?.deleteType === 'text' && existingMaterial) {
      return { ...TextInitialData, ...existingMaterial };
    }
    return TextInitialData;
  });
  const isInternalUpdate = useRef(false);

  const HandleChange = (value, name) => {
    isInternalUpdate.current = true;
    var fontWeight;
    if (name === 'fontFamily') {
      const selectedFont = localFonts.find((font) => font.family === value);
      fontWeight =
        name == 'fontFamily'
          ? selectedFont.variants.some((val) => val == 'regular')
            ? 'regular'
            : selectedFont?.variants[0]
          : addData.fontWeight;
    }

    setAddData((prevData) => ({
      ...prevData,
      fontWeight: fontWeight || addData.fontWeight,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (isGridHistorySuppressed()) return;
    if (!addData?.id || selectedObject?.deleteType !== 'text') return;
    if (addData === selectedObject?.userData?.text_material) return;

    const selectedMaterial = selectedObject?.userData?.text_material;
    if (
      selectedMaterial &&
      addData.id === selectedMaterial.id &&
      JSON.stringify({ ...addData, id: undefined }) ===
        JSON.stringify({ ...selectedMaterial, id: undefined })
    ) {
      return;
    }

    handleAddText(object, {
      ...addData,
      position: null,
      rotation: null,
      scale: null,
    });
  }, [addData]);

  useEffect(() => {
    const newMaterial = selectedObject?.userData?.text_material;
    if (selectedObject?.deleteType === 'text' && newMaterial) {
      if (isInternalUpdate.current) {
        isInternalUpdate.current = false;
        return;
      }
      const merged = { ...TextInitialData, ...newMaterial };
      if (JSON.stringify(merged) !== JSON.stringify(addData)) {
        setAddData(merged);
      }
    }
  }, [
    selectedObject?.uuid,
    selectedObject?.name,
    selectedObject?.userData?.text_material,
    historyUiRevision,
  ]);

  return (
    <>
      {/* <ToggleDropdown label="Text" toggle={open} setToggle={setOpen}> */}
      <div className="text-xs flex flex-col gap-1 py-2">
        <GridDropdown
          label="Text Type"
          value={addData.textType}
          onChange={(v) => HandleChange(v.target.value, 'textType')}
          options={[
            { value: '2D', label: '2D Text' },
            { value: '3D', label: '3D Text' },
          ]}
        />
        <GridTextInput
          key={
            selectedObject?.uuid ??
            selectedObject?.name ??
            selectedObject?.userData?.text_material?.id
          }
          label="Text"
          value={addData.content}
          onChange={(v) => HandleChange(v.target.value, 'content')}
          placeholder="Enter text here"
          className="w-full"
          multiline
        />
        <GridDropInput
          label="Font Family"
          value={addData.fontFamily}
          onChange={(v) => HandleChange(v.target.value, 'fontFamily')}
          options={localFonts.map((font) => ({
            value: font.family,
            label: `${font.family} (${font.type}) - ${font.description}`,
          }))}
          isFont
        />
        <GridDropdown
          label="Font Weight"
          value={addData.fontWeight}
          onChange={(v) => HandleChange(v.target.value, 'fontWeight')}
          options={localFonts
            .filter((font) => font.family === addData.fontFamily)[0]
            ?.variants.map((weight) => ({
              value: weight,
              label: getVariantLabel(weight),
            }))}
        />
        <EnhancedSlider
          toFix={0}
          label="Font Size"
          value={addData.fontSize}
          onChange={(value) => HandleChange(value, 'fontSize')}
          min={0}
          max={128}
          step={1}
          unit="px"
          maxEdit={false}
          restrictInput={true}
        />

        <ColorInput
          label={'Color'}
          color={addData.color}
          setColor={(value) => HandleChange(value, 'color')}
        />
        <MultiSelectionIcon
          label="Horizontal"
          selected={addData.horizontal}
          onClick={(value) => HandleChange(value, 'horizontal')}
          valueArr={[
            {
              value: 'right',
              label: 'Left',
              icon: 'horizontolLeft',
            },
            { value: 'center', label: 'Center', icon: 'horizontolCenter' },
            { value: 'left', label: 'Right', icon: 'horizontolRight' },
          ]}
        />
        <MultiSelectionIcon
          label="Vertical"
          selected={addData.vertical}
          onClick={(value) => HandleChange(value, 'vertical')}
          valueArr={[
            {
              value: 'top',
              label: 'Top',
              icon: 'verticalTop',
            },
            { value: 'center', label: 'Center', icon: 'verticalCenter' },
            { value: 'bottom', label: 'Bottom', icon: 'verticalBottom' },
          ]}
        />

        {addData.textType === '3D' && (
          <>
            <DividerRight />
            <EnhancedSlider
              toFix={2}
              label="Extrusion"
              value={addData.extrusion}
              onChange={(value) => HandleChange(value, 'extrusion')}
              min={0.001}
              max={0.05}
              step={0.0001}
              unit=""
              restrictInput={true}
            />

            <EnhancedSlider
              toFix={2}
              label="Bevel Size"
              value={addData.bevelSize}
              onChange={(value) => HandleChange(value, 'bevelSize')}
              min={0}
              max={0.1}
              step={0.01}
              unit=""
              restrictInput={true}
            />
            <EnhancedSlider
              toFix={0}
              label="Curve Segments"
              value={addData.curveSegments}
              onChange={(value) => HandleChange(value, 'curveSegments')}
              min={1}
              max={50}
              step={1}
              unit=""
              restrictInput={true}
            />
            <EnhancedSlider
              toFix={2}
              label="Metalness"
              value={addData.metalness}
              onChange={(value) => HandleChange(value, 'metalness')}
              min={0.1}
              max={1}
              step={0.01}
              unit=""
              restrictInput={true}
            />

            <EnhancedSlider
              toFix={2}
              label="Roughness"
              value={addData.roughness}
              onChange={(value) => HandleChange(value, 'roughness')}
              min={0.1}
              max={1}
              step={0.01}
              unit=""
              restrictInput={true}
            />
          </>
        )}
      </div>
      {/* </ToggleDropdown> */}
    </>
  );
};

export default TextComponent;
